import React, { useEffect, useState } from "react";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import {
  buildPreviousSeasonSalesByDate,
  fetchDashboardLedgerSummary,
  fetchDashboardStores,
  fetchPhaseDailySummary,
  fetchPreviousSeasonSales,
  getLedgerChartSalesBreakdown,
  getLedgerStoreIdsForLocation,
  isKpiPreviousSeasonEnabled,
} from "./dashboardBttApi";
import {
  buildBranchDailyKpiRows,
  DAILY_KPI_BRANCHES,
  formatDateLabel,
  normalizeDailyKpiResponse,
  normalizeDateKey,
} from "./kpiPerDayUtils";
import { kpiCategoryHoverPlugin } from "./kpiChartPlugins";
import useVietnamTodayKey from "./useVietnamTodayKey";
import "./KPIPerDayChart.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const formatQuantity = (value) =>
  Number(value || 0).toLocaleString("vi-VN", { maximumFractionDigits: 0 });

const formatShortDate = (dateKey) => {
  const [, month, day] = dateKey.split("-");
  return `${day}/${month}`;
};

const buildChartOptions = () => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    kpiCategoryHover: {
      backgroundColor: "rgba(47, 111, 184, 0.08)",
    },
    legend: {
      position: "bottom",
      labels: { usePointStyle: true, boxWidth: 9, padding: 18 },
    },
    tooltip: {
      callbacks: {
        label: (context) =>
          `${context.dataset.label}: ${formatQuantity(context.raw)}`,
      },
    },
  },
  scales: {
    x: {
      grid: {
        display: true,
        offset: true,
        color: "rgba(101, 128, 155, 0.2)",
        lineWidth: 1,
        drawTicks: false,
      },
      border: { display: false },
    },
    y: {
      beginAtZero: true,
      title: { display: true, text: "Số lượng bánh" },
      ticks: { callback: (value) => formatQuantity(value) },
    },
  },
});

const KPIPerDayChart = ({ ngayBatDau, ngayKetThuc, chiNhanh = [] }) => {
  const [branchCharts, setBranchCharts] = useState([]);
  const [effectiveRange, setEffectiveRange] = useState({ start: "", end: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const todayKey = useVietnamTodayKey();
  const previousSeasonEnabled = isKpiPreviousSeasonEnabled();

  const requestedStartDate = normalizeDateKey(ngayBatDau);
  const requestedEndDate = normalizeDateKey(ngayKetThuc);
  const branchIdsKey = (Array.isArray(chiNhanh) ? chiNhanh : [chiNhanh])
    .map(Number)
    .filter(Number.isFinite)
    .join(",");

  useEffect(() => {
    const controller = new AbortController();

    const loadChartData = async () => {
      if (
        requestedStartDate &&
        requestedEndDate &&
        requestedStartDate > requestedEndDate
      ) {
        setBranchCharts([]);
        setError("Khoảng thời gian xem KPI theo ngày không hợp lệ.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const requestedBranchIds = branchIdsKey
          ? branchIdsKey.split(",").map(Number)
          : DAILY_KPI_BRANCHES.map((branch) => branch.seasonBranchId);
        const [dailyResponse, storesResponse] = await Promise.all([
          fetchPhaseDailySummary({
            branchIds: requestedBranchIds,
            startDate: requestedStartDate,
            endDate: requestedEndDate,
            signal: controller.signal,
          }),
          fetchDashboardStores({ signal: controller.signal }).catch((storeError) => {
            if (storeError.name === "AbortError") throw storeError;
            console.warn("Could not load stores for daily KPI chart:", storeError);
            return [];
          }),
        ]);

        const dailyKpiRows = normalizeDailyKpiResponse(dailyResponse);
        const selectedBranches = DAILY_KPI_BRANCHES.filter((branch) =>
          requestedBranchIds.includes(branch.seasonBranchId)
        );
        const selectedDailyBranchIds = new Set(
          selectedBranches.map((branch) => branch.seasonBranchId)
        );
        const relevantDates = dailyKpiRows
          .filter((row) => selectedDailyBranchIds.has(Number(row.ChiNhanh)))
          .map((row) => row.Ngay)
          .filter(Boolean)
          .sort();
        const startDate = requestedStartDate || relevantDates[0] || "";
        const endDate =
          requestedEndDate || relevantDates[relevantDates.length - 1] || "";

        if (!startDate || !endDate || selectedBranches.length === 0) {
          setBranchCharts([]);
          setEffectiveRange({ start: startDate, end: endDate });
          setLoading(false);
          return;
        }

        const stores = Array.isArray(storesResponse) ? storesResponse : [];
        const hasDaNangBranch = selectedBranches.some(
          (branch) => branch.location === "danang"
        );
        const [previousSeasonResponse, charts] = await Promise.all([
          previousSeasonEnabled && hasDaNangBranch
            ? fetchPreviousSeasonSales({
                startDate,
                endDate,
                signal: controller.signal,
              })
            : Promise.resolve({ success: true, data: [] }),
          Promise.all(
            selectedBranches.map(async (branch) => {
              const storeIds = getLedgerStoreIdsForLocation(
                branch.location,
                stores
              );
              const phaseStartDates = dailyKpiRows
                .filter(
                  (row) =>
                    Number(row.ChiNhanh) === branch.seasonBranchId &&
                    row.NgayBatDauPhase &&
                    row.NgayKetThucPhase &&
                    row.NgayBatDauPhase <= endDate &&
                    row.NgayKetThucPhase >= startDate
                )
                .map((row) => row.NgayBatDauPhase);
              const calculationStartDate = [
                startDate,
                ...phaseStartDates,
              ].sort()[0];
              const ledgerSummary = await fetchDashboardLedgerSummary({
                storeIds,
                startDate: calculationStartDate,
                endDate,
                signal: controller.signal,
              });
              const actualByDate = new Map(
                (Array.isArray(ledgerSummary?.summaryByDate)
                  ? ledgerSummary.summaryByDate
                  : []
                ).map((item) => [
                  normalizeDateKey(item.NgayBan),
                  getLedgerChartSalesBreakdown(item).banhBan,
                ])
              );

              return {
                branch,
                rows: buildBranchDailyKpiRows({
                  branch,
                  dailyKpiRows,
                  actualByDate,
                  startDate: calculationStartDate,
                  endDate,
                  todayKey,
                }).filter((row) => row.date >= startDate),
              };
            })
          ),
        ]);
        const previousSeasonSalesByDate =
          buildPreviousSeasonSalesByDate(previousSeasonResponse);

        if (controller.signal.aborted) return;
        setEffectiveRange({ start: startDate, end: endDate });
        setBranchCharts(
          charts.map((chart) => ({
            ...chart,
            rows: chart.rows.map((row) => ({
              ...row,
              previousSeason:
                previousSeasonEnabled && chart.branch.location === "danang"
                  ? previousSeasonSalesByDate.get(row.date) || 0
                  : 0,
            })),
          }))
        );
      } catch (requestError) {
        if (requestError.name === "AbortError") return;
        console.error("Error fetching daily KPI chart:", requestError);
        setBranchCharts([]);
        setError("Không thể tải dữ liệu KPI theo ngày.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    loadChartData();
    return () => controller.abort();
  }, [
    branchIdsKey,
    previousSeasonEnabled,
    reloadKey,
    requestedEndDate,
    requestedStartDate,
    todayKey,
  ]);

  if (loading) {
    return (
      <section className="daily-kpi-chart daily-kpi-chart__state">
        <span className="spinner-border spinner-border-sm" aria-hidden="true" />
        Đang tải KPI theo ngày...
      </section>
    );
  }

  if (error) {
    return (
      <section className="daily-kpi-chart daily-kpi-chart__state is-error">
        <strong>{error}</strong>
        <button type="button" onClick={() => setReloadKey((value) => value + 1)}>
          Thử lại
        </button>
      </section>
    );
  }

  if (branchCharts.length === 0) {
    return (
      <section className="daily-kpi-chart daily-kpi-chart__state">
        Không có dữ liệu KPI theo ngày trong khoảng đã chọn.
      </section>
    );
  }

  return (
    <section className="daily-kpi-chart">
      <header className="daily-kpi-chart__header">
        <div>
          <span>Kế hoạch và thực tế</span>
          <h3>KPI theo ngày</h3>
        </div>
        <p>
          {formatDateLabel(effectiveRange.start, false)} – {formatDateLabel(effectiveRange.end, false)}
        </p>
      </header>

      <div className="daily-kpi-chart__branches">
        {branchCharts.map(({ branch, rows }) => {
          const totals = rows.reduce(
            (result, row) => ({
              original: result.original + row.originalKpi,
              actual: result.actual + row.actual,
              previousSeason: result.previousSeason + row.previousSeason,
              required: result.required + row.requiredKpi,
            }),
            { original: 0, actual: 0, previousSeason: 0, required: 0 }
          );
          const chartData = {
            labels: rows.map((row) => formatShortDate(row.date)),
            datasets: [
              {
                label: "KPI gốc",
                data: rows.map((row) => row.originalKpi),
                backgroundColor: "rgba(47, 111, 184, 0.72)",
                borderColor: "#2f6fb8",
                borderWidth: 1,
                borderRadius: 5,
              },
              {
                label: "Thực tế đạt",
                data: rows.map((row) => row.actual),
                backgroundColor: "rgba(29, 158, 111, 0.76)",
                borderColor: "#1d9e6f",
                borderWidth: 1,
                borderRadius: 5,
              },
              ...(previousSeasonEnabled && branch.location === "danang"
                ? [
                    {
                      label: "Số lượng mùa trước",
                      data: rows.map((row) => row.previousSeason),
                      backgroundColor: "rgba(124, 91, 196, 0.72)",
                      borderColor: "#6842b8",
                      borderWidth: 1,
                      borderRadius: 5,
                    },
                  ]
                : []),
            ],
          };

          return (
            <article className="daily-kpi-chart__branch" key={branch.dailyBranchId}>
              <div className="daily-kpi-chart__branch-title">
                <div>
                  <span>Chi nhánh</span>
                  <h4>{branch.name}</h4>
                </div>
                <b>{rows.length} ngày</b>
              </div>

              <div
                className={`daily-kpi-chart__totals${
                  previousSeasonEnabled && branch.location === "danang"
                    ? " has-previous-season"
                    : ""
                }`}
              >
                <div className="is-original">
                  <span>KPI gốc</span>
                  <strong>{formatQuantity(totals.original)}</strong>
                </div>
                <div className="is-actual">
                  <span>Thực tế đạt</span>
                  <strong>{formatQuantity(totals.actual)}</strong>
                </div>
                {previousSeasonEnabled && branch.location === "danang" && (
                  <div className="is-previous-season">
                    <span>Số lượng mùa trước</span>
                    <strong>{formatQuantity(totals.previousSeason)}</strong>
                  </div>
                )}
                {/* 
                <div className="is-required">
                  <span>KPI cần đạt</span>
                  <strong>{formatQuantity(totals.required)}</strong>
                </div>
                */}
              </div>

              <div className="daily-kpi-chart__scroll">
                <div
                  className="daily-kpi-chart__canvas"
                  style={{ minWidth: Math.max(720, rows.length * 76) }}
                >
                  <Bar
                    data={chartData}
                    options={buildChartOptions()}
                    plugins={[kpiCategoryHoverPlugin]}
                  />
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <footer className="daily-kpi-chart__source">
        KPI gốc lấy từ phân bổ của phase. Thực tế đạt chỉ lấy Bánh bán
        (LoaiPhieu = 1) từ dashboard ledger;
        {previousSeasonEnabled && (
          <> riêng Đà Nẵng có thêm số lượng mùa trước theo cùng ngày âm từ API
          SaleTrungThuRrevYear.</>
        )}{" "}
        KPI cần đạt được tính động ở frontend từ KPI_Goc và thực tế ledger;
        KPI_ThucSu lưu trong API không được trộn vào biểu đồ. Phần thiếu hoặc dư
        của ngày đã qua theo múi giờ Asia/Ho_Chi_Minh được phân bổ cho các ngày
        còn lại.
      </footer>
    </section>
  );
};

export default KPIPerDayChart;
